import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { useMedia } from 'react-use-media';
import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import { useTransition, animated as a } from 'react-spring';
import React, { useEffect, useState, useRef, useMemo } from 'react';
import { partial, differenceBy, isEmpty, forEach, range } from 'lodash';

import colours from '~/utils/colours';
import Todo from '~/App/Components/Todo/Simple';
import { Step } from '~/App/Components/Typeform';
import * as Ph from '~/App/Components/Placeholder';
import InputHelpBlock from './SendTo/InputHelpBlock';
import ActionButton from '~/App/Components/Typeform/ActionButton';
import useRollUpState from '~/App/Modules/TodoList/use-roll-up-state';
import { createFontAwesomeIcon } from '~/App/Components/Attributes/util';
import Scrollable from '~/App/Components/Typeform/Scrollable';

const StyledTodo = styled(Todo)``;

const RemoveTodoIcon = styled(createFontAwesomeIcon(faTimes))`
  color: ${colours.red};
`;

const RemoveTodo = ({ onClick }) => (
  <div
    style={{ padding: 10 }}
    onClick={onClick}
    aria-role='button'
    role='button'
  >
    <RemoveTodoIcon />
  </div>
);

RemoveTodo.propTypes = {
  onClick: PropTypes.func,
};

const TodoContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  border-bottom: 1px solid ${colours.greyMedium};

  &:hover {
    background-color: ${colours.greyLightAlt};
  }
`;

interface LoadingProps {
  count: number;
}

function Loading({ count }: { count: number }) {
  const isMobile: boolean = useMedia({ maxWidth: 768 });

  return (
    <Ph.Item>
      {range(count).map((_, i) => (
        <React.Fragment key={i}>
          <Ph.Col size={1}>
            <Ph.Avatar size={40} />
          </Ph.Col>
          <Ph.Col size={isMobile ? 10 : 11} style={{ paddingTop: 5 }}>
            <Ph.Row>
              <Ph.Col size={12} />
              <Ph.Col size={12} />
            </Ph.Row>
          </Ph.Col>
        </React.Fragment>
      ))}
    </Ph.Item>
  );
}

export default function RollUp({
  selectGreet,
  deselectGreet,
  deselectContact,
  greets,
  contactsAsProfiles,
  maxContacts,
  contactCount,
}) {
  const { rollUp, isLoading: isRollUpLoading } = useRollUpState();
  const [isLoading, setLoading] = useState(true);
  const canDelete = useMemo(() => greets.length > 2, [greets.length]);

  async function handleRemoveGreetClick(greetId: string) {
    await deselectGreet(greetId);
  }

  useEffect(() => {
    function updateProfiles() {
      // grab profiles from greets
      const greetProfiles = greets.reduce(
        (profiles, greet) => [...profiles, ...greet.profiles],
        []
      );

      // diff them with selected profiles
      const diff = differenceBy(contactsAsProfiles, greetProfiles, 'id');

      if (!isEmpty(diff)) {
        // remove the diff from selected profiles
        forEach(diff, (profile) => deselectContact(profile.id));
      }
    }

    updateProfiles();
  }, [greets.length]);

  // load all the profiles from the selected greets
  useEffect(() => {
    async function selectGreets() {
      setLoading(true);
      await Promise.all(rollUp.map(selectGreet));
      setLoading(false);
    }

    selectGreets();
  }, [rollUp]);

  const transitions = useTransition(isLoading || isRollUpLoading, null, {
    from: { width: '100%', position: 'absolute', opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
  });

  const ref = useRef<HTMLDivElement | null | undefined>(null);

  return (
    <Step heading='Bonjoros To Roll Up'>
      <Scrollable elementRef={ref.current} itemCount={greets.length}>
        {transitions.map(({ key, item: loading, props }) => {
          let children: React.ReactNode = null;

          if (loading) {
            children = <Loading count={rollUp.length} />;
          } else {
            children = greets.map((greet) => (
              <TodoContainer key={greet.id}>
                <StyledTodo
                  profiles={greet.profiles}
                  reason={greet.event?.message}
                  timestamp={greet.created_at}
                />
                {canDelete && (
                  <RemoveTodo
                    onClick={partial(handleRemoveGreetClick, greet.id)}
                  />
                )}
              </TodoContainer>
            ));
          }

          return (
            <a.div key={key} ref={ref} style={props}>
              {children}
            </a.div>
          );
        })}
      </Scrollable>
      {!isLoading && !isRollUpLoading && greets.length > maxContacts && (
        <InputHelpBlock maxContacts={maxContacts} contactCount={greets.length} />
      )}
      <ActionButton disabled={isLoading || isRollUpLoading} />
    </Step>
  );
}

RollUp.propTypes = {
  selectGreet: PropTypes.func,
  deselectGreet: PropTypes.func,
  deselectContact: PropTypes.func,
  greets: PropTypes.array,
  contactsAsProfiles: PropTypes.array,
  maxContacts: PropTypes.number,
  contactCount: PropTypes.number,
};
